import { useEffect, useState } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

function Goals() {
  const { hasPermission, user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentGoalForNotes, setCurrentGoalForNotes] = useState(null);
  const [tempNotes, setTempNotes] = useState("");
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [filters, setFilters] = useState({
    employeeName: "",
    projectName: "",
    deadline: "",
    createdDate: "", // Filter by creation date
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedEmployeeReport, setSelectedEmployeeReport] = useState(null);
  const [formData, setFormData] = useState({
    employee: "",
    projectGoals: [
      {
        project: "",
        projectName: "",
        goals: [{ name: "" }], // Start with just one goal
      },
    ],
  });
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [overdueNotificationShown, setOverdueNotificationShown] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const isAdmin = hasPermission("goals", "delete");
  const canCreateGoals = hasPermission("goals", "create");

  console.log("User permissions:", {
    isAdmin,
    canCreateGoals,
    user: user?.firstName + " " + user?.lastName,
    userId: user?.id || user?._id
  });

  // Function to check for overdue projects
  const checkOverdueProjects = () => {
    if (overdueNotificationShown) return; // Don't show multiple times

    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    const overdueProjects = [];
    const userOverdueGoals = [];

    goals.forEach(goal => {
      // Check single project deadline
      if (goal.project?.deadline) {
        const deadline = new Date(goal.project.deadline);
        if (deadline < today) {
          const projectInfo = {
            name: goal.project.name,
            deadline: goal.project.deadline,
            employee: `${goal.employee?.firstName} ${goal.employee?.lastName}`
          };

          if (isAdmin) {
            overdueProjects.push(projectInfo);
          } else if (goal.employee?._id === (user?.id || user?._id)) {
            userOverdueGoals.push(projectInfo);
          }
        }
      }

      // Check projects array deadlines
      if (goal.projects && goal.projects.length > 0) {
        goal.projects.forEach(proj => {
          if (proj.projectId?.deadline) {
            const deadline = new Date(proj.projectId.deadline);
            if (deadline < today) {
              const projectInfo = {
                name: proj.projectId.name || proj.projectName,
                deadline: proj.projectId.deadline,
                employee: `${goal.employee?.firstName} ${goal.employee?.lastName}`
              };

              if (isAdmin) {
                overdueProjects.push(projectInfo);
              } else if (goal.employee?._id === (user?.id || user?._id)) {
                userOverdueGoals.push(projectInfo);
              }
            }
          }
        });
      }
    });

    // Show notifications
    if (isAdmin && overdueProjects.length > 0) {
      const uniqueProjects = overdueProjects.reduce((acc, current) => {
        const existing = acc.find(item => item.name === current.name && item.employee === current.employee);
        return existing ? acc : [...acc, current];
      }, []);

      // Remove alert popup - just set notification flag for visual indicators
      setOverdueNotificationShown(true);
    } else if (!isAdmin && userOverdueGoals.length > 0) {
      const uniqueProjects = userOverdueGoals.reduce((acc, current) => {
        const existing = acc.find(item => item.name === current.name);
        return existing ? acc : [...acc, current];
      }, []);

      // Remove alert popup - just set notification flag for visual indicators
      setOverdueNotificationShown(true);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchGoals();
        await fetchProjects();
        if (isAdmin) {
          await fetchEmployees();
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for overdue projects after goals are loaded
  useEffect(() => {
    if (goals.length > 0 && !loading) {
      // Small delay to ensure UI is ready
      setTimeout(() => {
        checkOverdueProjects();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, loading]);

  const fetchGoals = async () => {
    try {
      const response = await api.get("/goals");
      setGoals(response.data);
    } catch (error) {
      console.error("Error fetching goals:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch goals";
      alert(`Error loading goals: ${errorMessage}`);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch employees";
      alert(`Error loading employees: ${errorMessage}`);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch projects";
      alert(`Error loading projects: ${errorMessage}`);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      // Create new project
      const response = await api.post("/projects", {
        name: newProjectName.trim(),
        description: "Created from Goals",
        status: "active"
      });

      const newProject = response.data;

      // Refresh projects list
      await fetchProjects();

      // Auto-select the new project
      console.log("Auto-selecting new project:", newProject);

      // We need to fetch projects again to get the full list including the new one, 
      // but optimally we can just append it locally if fetch is slow, 
      // however fetchProjects above handles it.

      // Add to selected projects
      // Note: newProject might need adaptation depending on API response structure
      // Assuming response.data is the project object or { project: ... }
      const projectData = newProject.project || newProject;

      handleProjectSelection(projectData._id, projectData.name);

      // Reset and close
      setNewProjectName("");
      setShowCreateProjectModal(false);
      setShowProjectSelector(false); // Close the dropdown as requested

      alert("✅ Project created and selected successfully!");
    } catch (error) {
      console.error("Error creating project:", error);
      alert(`Failed to create project: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Goal submission started", {
      editingGoalId,
      isAdmin,
      canCreateGoals,
      formData: JSON.stringify(formData)
    });

    // Check create permission for new goals
    if (!editingGoalId && !canCreateGoals) {
      alert("You don't have permission to create goals");
      return;
    }

    // Validation for non-editing mode
    if (!editingGoalId) {
      if (isAdmin && !formData.employee) {
        alert("Please select an employee");
        return;
      }

      const validProjects = formData.projectGoals.filter(
        (pg) => pg.project && pg.goals.some((g) => g.name && g.name.trim())
      );

      if (validProjects.length === 0) {
        alert("Please select at least one project and fill at least one goal");
        return;
      }
    }

    try {
      if (editingGoalId) {
        // Update existing goal
        const projectGoal = formData.projectGoals[0];

        // Validation for edit mode
        if (!projectGoal.project && !projectGoal.projectName) {
          alert("Please provide a project name or select a project");
          return;
        }

        // Filter out empty goals
        const validGoals = projectGoal.goals.filter((g) => g.name && g.name.trim());

        if (validGoals.length === 0) {
          alert("Please provide at least one valid goal");
          return;
        }

        const updateData = {
          employee: formData.employee,
          project: projectGoal.project,
          projectName: projectGoal.projectName,
          goals: validGoals.map((g) => ({
            _id: g._id || undefined, // Keep existing ID if available
            name: g.name.trim(),
            notes: g.notes || "",
            isCompleted: g.isCompleted || false,
            completedAt: g.completedAt || null
          })),
          isEditing: true,
        };

        console.log("Updating goal with data:", updateData); // Debug log
        const response = await api.put(`/goals/${editingGoalId}`, updateData);
        console.log("Goal updated successfully:", response.data);
        setEditingGoalId(null);

        // Show success message
        alert("Goal updated successfully!");
      } else {
        // Create new goals - ALWAYS create separate entries for each project
        let createdCount = 0;
        const errors = [];

        // Get valid projects with goals
        const validProjectGoals = formData.projectGoals.filter(
          (pg) => pg.project && pg.goals.some((g) => g.name && g.name.trim())
        );

        if (selectedProjects.length === 0 && validProjectGoals.length === 0) {
          alert("Please select at least one project and fill at least one goal");
          return;
        }

        // If we have selected projects via multi-select (new UI), use them
        // Otherwise use validProjectGoals from legacy structure

        let projectsToProcess = [];

        if (selectedProjects.length > 0) {
          // Use the first projectGoal as template for all selected projects
          const templateGoal = formData.projectGoals[0];
          const validTemplateGoals = templateGoal.goals.filter((g) => g.name && g.name.trim());

          if (validTemplateGoals.length === 0) {
            alert("Please provide at least one valid goal");
            return;
          }

          projectsToProcess = selectedProjects.map(sp => ({
            project: sp.id,
            projectName: sp.name,
            goals: validTemplateGoals
          }));
        } else {
          projectsToProcess = validProjectGoals;
        }

        // Create a separate goal entry for EACH project
        for (const [index, projectGoal] of projectsToProcess.entries()) {
          const validGoals = projectGoal.goals.filter((g) => g.name && g.name.trim());

          if (validGoals.length === 0) {
            continue; // Skip if no valid goals for this project
          }

          const submitData = {
            employee: isAdmin ? formData.employee : user.id,
            project: projectGoal.project,
            projectName: projectGoal.projectName,
            goals: validGoals.map((g) => ({
              name: g.name.trim(),
              notes: g.notes || "",
              isCompleted: false
            })),
          };

          try {
            console.log(`Creating goal entry ${index + 1} for project:`, submitData);
            const response = await api.post("/goals", submitData);
            console.log(`Goal entry ${index + 1} created successfully:`, response.data);
            createdCount++;
          } catch (err) {
            console.error(`Error creating goal for project ${index + 1}:`, err);
            errors.push(`${projectGoal.projectName || 'Project ' + (index + 1)}: ${err.response?.data?.message || err.message}`);
          }
        }

        // Show results
        if (errors.length > 0) {
          alert(`Some goals were created with errors:\n\n✅ ${createdCount} goal(s) created successfully\n❌ ${errors.length} error(s):\n${errors.join('\n')}`);
        } else if (createdCount > 0) {
          const projectWord = createdCount === 1 ? 'project' : 'projects';
          alert(`✅ Successfully created ${createdCount} goal ${createdCount === 1 ? 'entry' : 'entries'} for ${createdCount} ${projectWord}!`);
        } else {
          alert("No goals were created. Please check your inputs.");
          return;
        }
      }

      setShowModal(false);
      setSelectedProjects([]);
      setShowProjectSelector(false);
      setFormData({
        employee: "",
        projectGoals: [
          {
            project: "",
            projectName: "",
            goals: [{ name: "" }],
          },
        ],
      });
      fetchGoals();
    } catch (error) {
      console.error("Goal operation error:", error);
      const operation = editingGoalId ? "updating" : "creating";
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Error ${operation} goal`;
      alert(`Error ${operation} goal: ${errorMessage}`);

      // Don't close modal on error so user can fix and retry
      return;
    }
  };

  const startEditGoal = (goal) => {
    setEditingGoalId(goal._id);
    setFormData({
      employee: goal.employee._id,
      projectGoals: [
        {
          project: goal.project?._id || "",
          projectName: goal.projectName || goal.project?.name || "",
          goals: goal.goals.map((g) => ({
            _id: g._id,
            name: g.name,
            notes: g.notes || "",
            isCompleted: g.isCompleted || false,
            completedAt: g.completedAt || null
          })),
        },
      ],
    });
    setShowModal(true);
  };

  const cancelEdit = () => {
    setEditingGoalId(null);
    setShowModal(false);
    setSelectedProjects([]);
    setShowProjectSelector(false);
    setFormData({
      employee: "",
      projectGoals: [
        {
          project: "",
          projectName: "",
          goals: [{ name: "" }],
        },
      ],
    });
  };

  const handleProjectSelection = (projectId, projectName) => {
    const isSelected = selectedProjects.find(p => p.id === projectId);

    if (isSelected) {
      // Remove project
      const updatedSelectedProjects = selectedProjects.filter(p => p.id !== projectId);
      const updatedProjectGoals = formData.projectGoals.filter(pg => pg.project !== projectId);

      setSelectedProjects(updatedSelectedProjects);
      setFormData({
        ...formData,
        projectGoals: updatedProjectGoals.length > 0 ? updatedProjectGoals : [{
          project: "",
          projectName: "",
          goals: [{ name: "" }]
        }]
      });
    } else {
      // Add project
      const newProject = { id: projectId, name: projectName };
      const updatedSelectedProjects = [...selectedProjects, newProject];

      // Remove empty project goals and add the new one
      const filteredProjectGoals = formData.projectGoals.filter(pg => pg.project !== "");
      const updatedProjectGoals = [...filteredProjectGoals, {
        project: projectId,
        projectName: projectName,
        goals: [{ name: "" }]
      }];

      setSelectedProjects(updatedSelectedProjects);
      setFormData({
        ...formData,
        projectGoals: updatedProjectGoals
      });
    }

    // Auto-close dropdown after selection
    setShowProjectSelector(false);
  };

  const selectAllProjects = () => {
    const allProjectsData = projects.map(project => ({
      project: project._id,
      projectName: project.name,
      goals: [{ name: "" }]
    }));

    const allSelectedProjects = projects.map(project => ({
      id: project._id,
      name: project.name
    }));

    setSelectedProjects(allSelectedProjects);
    setFormData({
      ...formData,
      projectGoals: allProjectsData
    });
  };

  const clearAllProjects = () => {
    setSelectedProjects([]);
    setFormData({
      ...formData,
      projectGoals: [{
        project: "",
        projectName: "",
        goals: [{ name: "" }]
      }]
    });
  };

  const addProjectSection = () => {
    setShowProjectSelector(true);
  };

  const removeProjectSection = (index) => {
    const projectToRemove = formData.projectGoals[index];
    if (formData.projectGoals.length > 1) {
      const newProjectGoals = formData.projectGoals.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, projectGoals: newProjectGoals });

      // Also remove from selectedProjects
      setSelectedProjects(selectedProjects.filter(p => p.id !== projectToRemove.project));
    }
  };

  const updateProjectGoal = (index, field, value) => {
    const newProjectGoals = [...formData.projectGoals];
    newProjectGoals[index] = { ...newProjectGoals[index], [field]: value };
    setFormData({ ...formData, projectGoals: newProjectGoals });
  };

  const updateGoalInProject = (projectIndex, goalIndex, field, value) => {
    const newProjectGoals = [...formData.projectGoals];
    newProjectGoals[projectIndex].goals[goalIndex][field] = value;
    setFormData({ ...formData, projectGoals: newProjectGoals });
  };

  const addGoalToProject = (projectIndex) => {
    const newProjectGoals = [...formData.projectGoals];
    newProjectGoals[projectIndex].goals.push({ name: "" });
    setFormData({ ...formData, projectGoals: newProjectGoals });
  };

  const removeGoalFromProject = (projectIndex, goalIndex) => {
    const newProjectGoals = [...formData.projectGoals];
    if (newProjectGoals[projectIndex].goals.length > 1) {
      newProjectGoals[projectIndex].goals.splice(goalIndex, 1);
      setFormData({ ...formData, projectGoals: newProjectGoals });
    }
  };

  const getFilteredGoals = () => {
    return goals.filter((goal) => {
      const employeeName =
        `${goal.employee?.firstName} ${goal.employee?.lastName}`.toLowerCase();
      const projectName = (
        goal.projectName ||
        goal.project?.name ||
        ""
      ).toLowerCase();
      const deadline = goal.project?.deadline
        ? new Date(goal.project.deadline).toLocaleDateString()
        : "";

      const matchesEmployee = employeeName.includes(
        filters.employeeName.toLowerCase()
      );
      const matchesProject = projectName.includes(
        filters.projectName.toLowerCase()
      );
      const matchesDeadline =
        !filters.deadline || deadline === filters.deadline;

      // Date filter - filter by creation date
      let matchesDate = true;
      if (filters.createdDate) {
        const goalDate = new Date(goal.createdAt);
        const filterDate = new Date(filters.createdDate);
        goalDate.setHours(0, 0, 0, 0);
        filterDate.setHours(0, 0, 0, 0);
        matchesDate = goalDate.getTime() === filterDate.getTime();
      }

      return matchesEmployee && matchesProject && matchesDeadline && matchesDate;
    });
  };

  const handleGoalToggle = (goalId, goalItemId, isCompleted, goalOwnerId, goalItem) => {
    // Check if user can modify this goal (own goal or admin)
    const ownerIdStr = goalOwnerId ? String(goalOwnerId) : null;
    const currentUserId = user?.id || user?._id || null;
    if (!isAdmin && ownerIdStr !== String(currentUserId)) {
      alert("You can only modify your own goals");
      return;
    }

    // Prevent toggling if the goal wasn't created today
    const goalObj = goals.find((g) => g._id === goalId);
    if (goalObj) {
      const created = new Date(goalObj.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      created.setHours(0, 0, 0, 0);
      if (String(created) !== String(today) && !isAdmin) {
        alert("You can only modify goals on the day they are created");
        return;
      }
    }

    // Show notes modal for goal update
    setCurrentGoalForNotes({
      goalId,
      goalItemId,
      isCompleted: !isCompleted, // Toggle the completion status
      goalItem,
      goalObj
    });
    setTempNotes(goalItem.notes || "");
    setShowNotesModal(true);
  };

  const handleNotesModalConfirm = async () => {
    try {
      const { goalId, goalItemId, isCompleted, goalObj } = currentGoalForNotes;

      // Update the goal with new completion status and notes
      const updatedGoals = goalObj.goals.map((g) => ({
        _id: g._id,
        name: g.name,
        isCompleted: g._id === goalItemId ? isCompleted : g.isCompleted,
        notes: g._id === goalItemId ? tempNotes : (g.notes || ""),
        completedAt: g._id === goalItemId && isCompleted ? new Date() : g.completedAt
      }));

      const updateData = {
        goals: updatedGoals,
        isEditing: false // This is a completion toggle with notes
      };

      console.log('Updating goal with data:', updateData);
      const response = await api.put(`/goals/${goalId}`, updateData);
      console.log('Goal updated successfully:', response.data);

      fetchGoals(); // Refresh the goals list
      setShowNotesModal(false);
      setCurrentGoalForNotes(null);
      setTempNotes("");

      // Show success message
      const action = isCompleted ? 'completed' : 'unchecked';
      alert(`Goal ${action} successfully!`);
    } catch (error) {
      console.error('Error updating goal:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating goal';
      alert(`Error updating goal: ${errorMessage}`);
    }
  };

  const confirmGoalUpdate = async (goalId) => {
    try {
      const goal = goals.find((g) => g._id === goalId);
      const pendingGoalUpdates = pendingUpdates[goalId] || {};

      const updatedGoals = goal.goals.map((g) => ({
        ...g,
        isCompleted: pendingGoalUpdates.hasOwnProperty(g._id)
          ? pendingGoalUpdates[g._id]
          : g.isCompleted,
      }));

      await api.put(`/goals/${goalId}`, { goals: updatedGoals });

      // Clear pending updates for this goal
      setPendingUpdates((prev) => {
        const newPending = { ...prev };
        delete newPending[goalId];
        return newPending;
      });

      fetchGoals();
    } catch (error) {
      console.error("Error updating goal:", error);
      alert(error.response?.data?.message || "Error updating goal");
    }
  };

  const cancelGoalUpdate = (goalId) => {
    setPendingUpdates((prev) => {
      const newPending = { ...prev };
      delete newPending[goalId];
      return newPending;
    });
  };

  const generateMonthlyReport = (employeeId, employeeName, selectedMonth = null, selectedYear = null) => {
    // Get current month and year or use selected ones
    const now = new Date();
    const currentMonth = selectedMonth !== null ? selectedMonth : now.getMonth();
    const currentYear = selectedYear !== null ? selectedYear : now.getFullYear();

    // Get all goals for this employee in current month
    const employeeGoals = goals.filter(goal => {
      const goalEmployee = goal.employee?._id || goal.employee;
      const goalDate = new Date(goal.createdAt);
      return String(goalEmployee) === String(employeeId) &&
        goalDate.getMonth() === currentMonth &&
        goalDate.getFullYear() === currentYear;
    });

    // Get number of days in current month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    // Create day-wise report
    const dailyReport = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateStr = date.toISOString().split('T')[0];

      // Find goals created on this day
      const dayGoals = employeeGoals.filter(goal => {
        const goalDate = new Date(goal.createdAt);
        return goalDate.getDate() === day;
      });

      let completed = 0;
      let total = 0;
      let missed = 0;

      dayGoals.forEach(goal => {
        goal.goals.forEach(item => {
          total++;
          if (item.isCompleted) {
            completed++;
          } else {
            missed++;
          }
        });
      });

      if (day <= now.getDate()) {
        dailyReport.push({
          day,
          date: dateStr,
          dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
          completed,
          missed,
          total,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0
        });
      }
    }

    return {
      employeeName,
      employeeId,
      month: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      dailyReport,
      totalCompleted: dailyReport.reduce((sum, day) => sum + day.completed, 0),
      totalMissed: dailyReport.reduce((sum, day) => sum + day.missed, 0),
      totalGoals: dailyReport.reduce((sum, day) => sum + day.total, 0),
      overallPercentage: dailyReport.reduce((sum, day) => sum + day.total, 0) > 0
        ? Math.round((dailyReport.reduce((sum, day) => sum + day.completed, 0) / dailyReport.reduce((sum, day) => sum + day.total, 0)) * 100)
        : 0
    };
  };

  const viewEmployeeReport = (employeeId, employeeName) => {
    const now = new Date();
    const report = generateMonthlyReport(employeeId, employeeName, now.getMonth(), now.getFullYear());
    setSelectedEmployeeReport({
      ...report,
      employeeId,
      selectedMonth: now.getMonth(),
      selectedYear: now.getFullYear()
    });
    setShowReportModal(true);
  };

  const changeReportMonth = (direction) => {
    if (!selectedEmployeeReport) return;

    let newMonth = selectedEmployeeReport.selectedMonth + direction;
    let newYear = selectedEmployeeReport.selectedYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    const report = generateMonthlyReport(
      selectedEmployeeReport.employeeId,
      selectedEmployeeReport.employeeName,
      newMonth,
      newYear
    );

    setSelectedEmployeeReport({
      ...report,
      employeeId: selectedEmployeeReport.employeeId,
      selectedMonth: newMonth,
      selectedYear: newYear
    });
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes blink {
            0% { opacity: 1; }
            50% { opacity: 0.3; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <div className="container">
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "12px"
        }}>
          <h1 style={{ margin: 0, fontSize: "clamp(20px, 5vw, 28px)", fontWeight: "700", whiteSpace: "nowrap" }}>Goals</h1>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 auto", justifyContent: "flex-end", flexWrap: "wrap", minWidth: "280px" }}>
            {/* Employee Filter - Only for Admin */}
            {isAdmin && (
              <select
                value={filters.employeeName}
                onChange={(e) => setFilters({ ...filters, employeeName: e.target.value })}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "13px",
                  minWidth: "130px",
                  height: "38px",
                  cursor: "pointer",
                  background: "white",
                }}
              >
                <option value="">All Employees</option>
                {[...new Set(goals.map(g => `${g.employee?.firstName} ${g.employee?.lastName}`))].map((name, idx) => (
                  <option key={idx} value={name}>{name}</option>
                ))}
              </select>
            )}

            {/* Project Filter */}
            <select
              value={filters.projectName}
              onChange={(e) => setFilters({ ...filters, projectName: e.target.value })}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "13px",
                minWidth: "100px",
                height: "38px",
                cursor: "pointer",
                background: "white",
              }}
            >
              <option value="">All Projects</option>
              {[...new Set(goals.map(g => g.projectName || g.project?.name).filter(Boolean))].map((name, idx) => (
                <option key={idx} value={name}>{name}</option>
              ))}
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={filters.createdDate}
              onChange={(e) => setFilters({ ...filters, createdDate: e.target.value })}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "13px",
                height: "38px",
                cursor: "pointer",
                background: "white",
                minWidth: "130px",
              }}
            />

            {/* View Toggle Buttons */}
            <button
              onClick={() => setViewMode('card')}
              style={{
                padding: "8px 14px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                background: viewMode === 'card' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                color: viewMode === 'card' ? 'white' : '#64748b',
                transition: "all 0.2s",
                height: "38px",
                whiteSpace: "nowrap",
              }}
            >
              🧩 Card View
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: "8px 14px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                background: viewMode === 'table' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                color: viewMode === 'table' ? 'white' : '#64748b',
                transition: "all 0.2s",
                height: "38px",
                whiteSpace: "nowrap",
              }}
            >
              📋 Table View
            </button>

            {canCreateGoals && (
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
                style={{
                  whiteSpace: "nowrap",
                  height: "38px",
                }}
              >
                Create Goal
              </button>
            )}
          </div>
        </div>

        <div>
          {loading ? (
            <div className="card text-center" style={{ padding: "40px" }}>
              <p style={{ fontSize: "18px", color: "#666" }}>Loading goals...</p>
              <div style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "20px auto"
              }}></div>
            </div>
          ) : (
            <>
              {/* Removed empty JSX block that caused syntax error */}

              {/* Card View */}
              {viewMode === 'card' && getFilteredGoals().map((goal) => (
                <div key={goal._id} className="card">
                  <div className="flex-between">
                    <div style={{ flex: 1 }}>
                      <h3 style={{ marginBottom: "8px" }}>
                        👤 {goal.employee?.firstName} {goal.employee?.lastName}{" "}
                        <span style={{ fontSize: "14px", color: "#999" }}>
                          ({goal.employee?.employeeId})
                        </span>
                      </h3>
                      <div
                        style={{
                          marginBottom: "12px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {/* Display multiple projects if they exist */}
                        {goal.projects && goal.projects.length > 0 ? (
                          <div>
                            <h4 style={{ margin: 0, color: "#667eea", marginBottom: "8px" }}>
                              📋 Projects ({goal.projects.length}):
                            </h4>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                              {goal.projects.map((proj, index) => {
                                const isOverdue = proj.projectId?.deadline &&
                                  new Date(proj.projectId.deadline) < new Date();

                                return (
                                  <span
                                    key={index}
                                    style={{
                                      padding: "4px 12px",
                                      background: isOverdue ? "#ffe6e6" : "#f0f4ff",
                                      border: `1px solid ${isOverdue ? "#dc3545" : "#667eea"}`,
                                      borderRadius: "16px",
                                      fontSize: "12px",
                                      color: isOverdue ? "#dc3545" : "#667eea",
                                      fontWeight: "600",
                                      position: "relative"
                                    }}
                                  >
                                    {proj.projectName || proj.projectId?.name || "Unknown Project"}
                                    {isOverdue && (
                                      <span style={{
                                        marginLeft: "6px",
                                        animation: "blink 1s infinite"
                                      }}>
                                        ⚠️
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          /* Single project display (backward compatibility) */
                          <h4 style={{ margin: 0, color: "#667eea" }}>
                            📋{" "}
                            {goal.projectName ||
                              goal.project?.name ||
                              "Unknown Project"}
                          </h4>
                        )}

                        {isAdmin && (
                          <span style={{ color: "#999", fontSize: "12px" }}>
                            Role: {goal.employee?.role?.name || "N/A"}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: "8px",
                          display: "flex",
                          gap: "15px",
                          fontSize: "13px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: "#666" }}>
                          📅 Start:{" "}
                          {goal.project?.startDate
                            ? new Date(goal.project.startDate).toLocaleDateString()
                            : "N/A"}
                        </span>
                        <span style={{
                          color: goal.project?.deadline && new Date(goal.project.deadline) < new Date()
                            ? "#dc3545" : "#666"
                        }}>
                          ⏰ Deadline:{" "}
                          {goal.project?.deadline
                            ? new Date(goal.project.deadline).toLocaleDateString()
                            : "N/A"}
                          {goal.project?.deadline && new Date(goal.project.deadline) < new Date() && (
                            <span style={{
                              marginLeft: "8px",
                              color: "#dc3545",
                              fontWeight: "bold",
                              animation: "blink 1s infinite"
                            }}>
                              ⚠️ OVERDUE
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: "600",
                            background:
                              goal.project?.status === "active"
                                ? "#d4edda"
                                : goal.project?.status === "completed"
                                  ? "#d1ecf1"
                                  : "#fff3cd",
                            color:
                              goal.project?.status === "active"
                                ? "#155724"
                                : goal.project?.status === "completed"
                                  ? "#0c5460"
                                  : "#856404",
                          }}
                        >
                          {goal.project?.status || "N/A"}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => startEditGoal(goal)}
                        style={{ padding: "8px 16px", fontSize: "14px" }}
                        title="Edit goal and project"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => viewEmployeeReport(goal.employee?._id, `${goal.employee?.firstName} ${goal.employee?.lastName}`)}
                        style={{ padding: "8px 16px", fontSize: "14px", background: "#28a745", borderColor: "#28a745" }}
                        title="View monthly report"
                      >
                        📊 View Report
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: "20px" }}>
                    <h4 style={{ marginBottom: "15px" }}>Goals:</h4>
                    {goal.goals.map((item) => {
                      const created = new Date(goal.createdAt);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      created.setHours(0, 0, 0, 0);
                      const createdToday = String(created) === String(today);

                      const canModify =
                        isAdmin ||
                        (String(goal.employee?._id) ===
                          String(user?.id || user?._id) &&
                          createdToday);

                      return (
                        <div
                          key={item._id}
                          className="checkbox-container"
                          style={{ marginBottom: "10px" }}
                        >
                          <input
                            type="checkbox"
                            checked={item.isCompleted}
                            onChange={() =>
                              handleGoalToggle(
                                goal._id,
                                item._id,
                                item.isCompleted,
                                goal.employee?._id,
                                item
                              )
                            }
                            disabled={!canModify}
                          />
                          <label
                            style={{
                              textDecoration: item.isCompleted ? "line-through" : "none",
                            }}
                          >
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                              {item.name}
                            </div>
                            {item.notes && (
                              <div style={{
                                fontSize: '12px',
                                color: '#666',
                                fontStyle: 'italic',
                                marginLeft: '20px',
                                marginTop: '4px'
                              }}>
                                📝 {item.notes}
                              </div>
                            )}
                            {item.isCompleted && item.completedAt && (
                              <span
                                style={{
                                  marginLeft: "10px",
                                  fontSize: "12px",
                                  color: "#28a745",
                                }}
                              >
                                (Completed on{" "}
                                {new Date(item.completedAt).toLocaleDateString()})
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: "15px", fontSize: "14px", color: "#666" }}>
                    Created on {new Date(goal.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}

              {/* Table View */}
              {viewMode === 'table' && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Employee</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Project(s)</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Goals</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Progress</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>Created</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredGoals().map((goal, index) => {
                          const completedCount = goal.goals.filter(g => g.isCompleted).length;
                          const totalCount = goal.goals.length;
                          const progressPercent = (completedCount / totalCount) * 100;
                          const created = new Date(goal.createdAt);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          created.setHours(0, 0, 0, 0);
                          const createdToday = String(created) === String(today);
                          const canModify = isAdmin || (String(goal.employee?._id) === String(user?.id || user?._id) && createdToday);

                          return (
                            <tr key={goal._id} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: 'white',
                              transition: 'background 0.2s'
                            }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f4ff'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                                <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                                  👤 {goal.employee?.firstName} {goal.employee?.lastName}
                                </div>
                                <div style={{ fontSize: '12px', color: '#999' }}>
                                  {goal.employee?.employeeId}
                                </div>
                                {isAdmin && (
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                    {goal.employee?.role?.name || 'N/A'}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                                {goal.projects && goal.projects.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {goal.projects.map((proj, idx) => {
                                      const isOverdue = proj.projectId?.deadline && new Date(proj.projectId.deadline) < new Date();
                                      return (
                                        <span key={idx} style={{
                                          padding: '3px 8px',
                                          background: isOverdue ? '#ffe6e6' : '#f0f4ff',
                                          border: `1px solid ${isOverdue ? '#dc3545' : '#667eea'}`,
                                          borderRadius: '12px',
                                          fontSize: '11px',
                                          color: isOverdue ? '#dc3545' : '#667eea',
                                          fontWeight: '600',
                                          display: 'inline-block'
                                        }}>
                                          {proj.projectName || proj.projectId?.name || 'Unknown'}
                                          {isOverdue && ' ⚠️'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{ color: '#667eea', fontWeight: '500', fontSize: '13px' }}>
                                    📋 {goal.projectName || goal.project?.name || 'Unknown Project'}
                                  </div>
                                )}
                                {goal.project?.deadline && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: new Date(goal.project.deadline) < new Date() ? '#dc3545' : '#666',
                                    marginTop: '4px',
                                    fontWeight: new Date(goal.project.deadline) < new Date() ? '600' : 'normal'
                                  }}>
                                    ⏰ {new Date(goal.project.deadline).toLocaleDateString()}
                                    {new Date(goal.project.deadline) < new Date() && ' (OVERDUE)'}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px 4px', verticalAlign: 'top' }}>
                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                  {goal.goals.map((item, idx) => (
                                    <div key={item._id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        checked={item.isCompleted}
                                        onChange={() => handleGoalToggle(goal._id, item._id, item.isCompleted, goal.employee?._id, item)}
                                        disabled={!canModify}
                                        style={{ marginTop: '2px', cursor: canModify ? 'pointer' : 'not-allowed' }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <div style={{
                                          fontSize: '12px',
                                          color: item.isCompleted ? '#999' : '#333',
                                          textDecoration: item.isCompleted ? 'line-through' : 'none',
                                          fontWeight: item.isCompleted ? 'normal' : '500'
                                        }}>
                                          {item.name}
                                        </div>
                                        {item.notes && (
                                          <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>
                                            📝 {item.notes}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td style={{ padding: '15px 8px', verticalAlign: 'top' }}>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: goal.project?.status === 'active' ? '#d4edda' :
                                    goal.project?.status === 'completed' ? '#d1ecf1' : '#fff3cd',
                                  color: goal.project?.status === 'active' ? '#155724' :
                                    goal.project?.status === 'completed' ? '#0c5460' : '#856404',
                                }}>
                                  {goal.project?.status || 'N/A'}
                                </span>
                              </td>
                              <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                                <div style={{ minWidth: '120px' }}>
                                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                                    {completedCount} / {totalCount} completed
                                  </div>
                                  <div style={{
                                    width: '100%',
                                    height: '8px',
                                    background: '#e2e8f0',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      width: `${progressPercent}%`,
                                      height: '100%',
                                      background: progressPercent === 100 ? '#28a745' :
                                        progressPercent >= 50 ? '#ffc107' : '#667eea',
                                      transition: 'width 0.3s ease'
                                    }}></div>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                    {Math.round(progressPercent)}%
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  {new Date(goal.createdAt).toLocaleDateString()}
                                </div>
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center', verticalAlign: 'top' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                                  <button
                                    onClick={() => startEditGoal(goal)}
                                    style={{
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      transition: 'transform 0.2s',
                                      whiteSpace: 'nowrap'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => viewEmployeeReport(goal.employee?._id, `${goal.employee?.firstName} ${goal.employee?.lastName}`)}
                                    style={{
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      transition: 'transform 0.2s',
                                      whiteSpace: 'nowrap'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                  >
                                    📊 Report
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {getFilteredGoals().length === 0 && goals.length > 0 && (
                <div className="card text-center">
                  <p>No goals match the selected filters</p>
                </div>
              )}

              {goals.length === 0 && (
                <div className="card text-center">
                  <p>No goals available</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Styles */}
        <div className="modal-styles">
          <style>{`
            .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: rgba(0, 0, 0, 0.4);
              backdrop-filter: blur(4px);
              display: flex;
              justify-content: center;
              align-items: center;
              z-index: 1050;
            }
            .clean-modal {
              background: #ffffff;
              border-radius: 8px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
              max-width: 650px;
              width: 90%;
              max-height: 90vh;
              display: flex;
              flex-direction: column;
              overflow: hidden;
              animation: slideIn 0.2s ease-out;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            }
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .modal-header-clean {
              padding: 16px 24px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 1px solid #e5e7eb;
            }
            .modal-title-clean {
              font-size: 18px;
              font-weight: 600;
              color: #1f2937;
              margin: 0;
            }
            .close-btn-clean {
              background: transparent;
              border: none;
              color: #9ca3af;
              font-size: 20px;
              cursor: pointer;
              padding: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
            }
            .close-btn-clean:hover {
              background-color: #f3f4f6;
              color: #4b5563;
            }
            .modal-content-clean {
              padding: 24px;
              overflow-y: auto;
              flex: 1;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f1f1;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #d1d5db;
              border-radius: 3px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #9ca3af;
            }
            .form-group-clean {
              margin-bottom: 20px;
            }
            .label-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
            }
            .label-clean {
              font-size: 14px;
              font-weight: 500;
              color: #374151;
            }
            .label-clean span.required {
              color: #dc2626;
              margin-left: 2px;
            }
            .input-clean, .select-clean {
              width: 100%;
              padding: 10px 12px;
              background-color: #f9fafb;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              font-size: 14px;
              color: #111827;
              outline: none;
              transition: border-color 0.15s;
              box-sizing: border-box;
            }
            .input-clean:focus, .select-clean:focus {
              background-color: #ffffff;
              border-color: #4a7c59;
              box-shadow: 0 0 0 3px rgba(74, 124, 89, 0.1);
            }
            .btn-action-green {
              background-color: #43795b;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 6px 12px;
              font-size: 12px;
              font-weight: 500;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 4px;
            }
            .btn-action-green:hover {
              background-color: #366048;
            }
            .btn-add-project {
               background-color: #43795b;
               color: white;
               border: none;
               border-radius: 4px;
               padding: 6px 16px; 
               font-size: 13px;
               font-weight: 500;
               cursor: pointer;
            }
            .btn-footer {
              padding: 10px 24px;
              border-radius: 6px;
              font-weight: 500;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;
            }
            .btn-footer-cancel {
              background: #f3f4f6;
              border: 1px solid #e5e7eb;
              color: #374151;
            }
            .btn-footer-cancel:hover {
               background: #e5e7eb;
            }
            .btn-footer-primary {
              background: #43795b;
              border: 1px solid #43795b;
              color: white;
            }
            .btn-footer-primary:hover {
               background: #366048;
               box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            }
            .project-selector {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px;
              marginTop: 8px;
              max-height: 200px;
              overflow-y: auto;
              background: white;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
            .project-item {
              padding: 10px;
              border: 1px solid #e5e7eb;
              border-radius: 6px;
              margin-bottom: 6px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 14px;
              transition: all 0.15s;
            }
            .project-item:hover {
               background-color: #f9fafb;
            }
            .project-item.selected {
               background-color: #f0fdf4;
               border-color: #bbf7d0;
               color: #166534;
            }
            .selected-tag {
              background: #f0fdf4;
              border: 1px solid #bbf7d0;
              border-radius: 6px;
              padding: 6px 10px;
              font-size: 13px;
              color: #15803d;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            .project-label-tag {
              background: #f3f4f6;
              color: #6b7280;
              font-size: 11px;
              padding: 2px 8px;
              border-radius: 12px;
              font-weight: 500;
              border: 1px solid #e5e7eb;
            }
          `}</style>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => cancelEdit()}>
            <div className="clean-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-clean">
                <h2 className="modal-title-clean">
                  {editingGoalId ? "Edit Goal" : "New Goal"}
                </h2>
                <button className="close-btn-clean" onClick={() => cancelEdit()}>
                  ✕
                </button>
              </div>

              <div className="modal-content-clean custom-scrollbar">
                <form onSubmit={handleSubmit}>
                  {!editingGoalId && isAdmin && (
                    <div className="form-group-clean">
                      <div className="label-row">
                        <label className="label-clean">
                          Assign To Employee <span className="required">*</span>
                        </label>
                      </div>
                      <select
                        className="select-clean"
                        value={formData.employee}
                        onChange={(e) => setFormData({ ...formData, employee: e.target.value })}
                        required
                      >
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.firstName} {emp.lastName} ({emp.employeeId})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!editingGoalId && (
                    <div className="form-group-clean">
                      <div className="label-row">
                        <label className="label-clean">Select Project <span className="required">*</span></label>
                        <button
                          type="button"
                          className="btn-add-project"
                          onClick={() => setShowProjectSelector(!showProjectSelector)}
                        >
                          + Add Project
                        </button>
                      </div>

                      {!showProjectSelector && selectedProjects.length === 0 && (
                        <div
                          className="input-clean"
                          style={{ color: '#9ca3af', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onClick={() => setShowProjectSelector(true)}
                        >
                          <span>Select the project for this goal</span>
                          <span style={{ fontSize: '12px' }}>▼</span>
                        </div>
                      )}

                      {!showProjectSelector && selectedProjects.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
                          {selectedProjects.map(p => (
                            <div key={p.id} className="selected-tag">
                              {p.name}
                              <span onClick={() => handleProjectSelection(p.id, p.name)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>✕</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {showProjectSelector && (
                        <div className="project-selector">
                          <div
                            className="project-item"
                            onClick={() => {
                              setShowCreateProjectModal(true);
                              // Keep selector open to show context, or close it? 
                              // User request: "dropdown should auto-close... After project added".
                              // So we can keep it open or close it. Usually keeping it open behind modal is fine.
                            }}
                            style={{ borderBottom: '1px solid #f3f4f6', marginBottom: '8px', color: '#43795b', fontWeight: '600' }}
                          >
                            <div style={{
                              width: '18px', height: '18px', borderRadius: '4px', border: '1px solid #43795b',
                              background: '#43795b', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'white', fontSize: '14px'
                            }}>
                              +
                            </div>
                            <span>Create New Project</span>
                          </div>
                          {projects.map((project) => {
                            const isSelected = selectedProjects.find(p => p.id === project._id);
                            return (
                              <div
                                key={project._id}
                                className={`project-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleProjectSelection(project._id, project.name)}
                              >
                                <div style={{
                                  width: '18px', height: '18px', borderRadius: '4px', border: '1px solid #d1d5db',
                                  background: isSelected ? '#43795b' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: 'white', fontSize: '12px'
                                }}>
                                  {isSelected && '✓'}
                                </div>
                                <span>{project.name}</span>
                              </div>
                            );
                          })}
                          <div style={{ textAlign: 'right', marginTop: '12px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
                            <button type="button" onClick={() => setShowProjectSelector(false)} style={{ background: 'none', border: 'none', color: '#43795b', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {formData.projectGoals.filter(pg => pg.project || editingGoalId).map((projectGoal, projectIndex) => (
                    <div key={projectIndex}>
                      <div className="label-row" style={{ marginTop: '24px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label className="label-clean">Goal Title <span className="required">*</span></label>
                          <span className="project-label-tag">
                            {projectGoal.projectName || 'Project'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-action-green"
                          onClick={() => addGoalToProject(projectIndex)}
                        >
                          + Add Goal
                        </button>
                      </div>

                      {projectGoal.goals.map((goal, goalIndex) => (
                        <div key={goalIndex} style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                          <input
                            type="text"
                            className="input-clean"
                            value={goal.name ? goal.name : ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              updateGoalInProject(projectIndex, goalIndex, "name", newValue);
                            }}
                            placeholder="Enter the title of the goal"
                            autoFocus={projectIndex === 0 && goalIndex === 0}
                          />
                          {projectGoal.goals.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeGoalFromProject(projectIndex, goalIndex)}
                              style={{
                                background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca',
                                borderRadius: '6px', width: '38px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}
                              title="Remove Goal"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  <div style={{
                    marginTop: "32px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    paddingTop: "24px",
                    borderTop: "1px solid #e5e7eb"
                  }}>
                    <button
                      type="button"
                      className="btn-footer btn-footer-cancel"
                      onClick={() => cancelEdit()}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-footer btn-footer-primary">
                      {editingGoalId ? "Save Changes" : "Create Goal"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateProjectModal && (
          <div className="modal-overlay" onClick={() => setShowCreateProjectModal(false)}>
            <div className="clean-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', height: 'auto', minHeight: 'auto' }}>
              <div className="modal-header-clean">
                <h2 className="modal-title-clean">Create New Project</h2>
                <button className="close-btn-clean" onClick={() => setShowCreateProjectModal(false)}>✕</button>
              </div>
              <form onSubmit={handleCreateProject}>
                <div className="modal-content-clean" style={{ paddingBottom: '0' }}>
                  <div className="form-group-clean">
                    <label className="label-clean">Project Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input-clean"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder="e.g. Website Redesign"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button type="button" className="btn-footer btn-footer-cancel" onClick={() => setShowCreateProjectModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-footer btn-footer-primary">
                    Create Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Monthly Report Modal */}
        {showReportModal && selectedEmployeeReport && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              overflow: "auto",
              padding: "20px"
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "12px",
                minWidth: "800px",
                maxWidth: "1000px",
                maxHeight: "90vh",
                overflow: "auto",
                boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '2px solid #667eea', paddingBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: 0, color: "#667eea", fontSize: '24px' }}>
                    📊 Monthly Goals Report
                  </h2>
                  <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                    {selectedEmployeeReport.employeeName}
                  </p>
                </div>

                {/* Month Navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <button
                    onClick={() => changeReportMonth(-1)}
                    style={{
                      background: "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                    title="Previous Month"
                  >
                    ◄
                  </button>

                  <div style={{
                    textAlign: 'center',
                    minWidth: '150px',
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}>
                    {selectedEmployeeReport.month}
                  </div>

                  <button
                    onClick={() => changeReportMonth(1)}
                    disabled={selectedEmployeeReport.selectedMonth === new Date().getMonth() && selectedEmployeeReport.selectedYear === new Date().getFullYear()}
                    style={{
                      background: selectedEmployeeReport.selectedMonth === new Date().getMonth() && selectedEmployeeReport.selectedYear === new Date().getFullYear() ? "#ccc" : "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      cursor: selectedEmployeeReport.selectedMonth === new Date().getMonth() && selectedEmployeeReport.selectedYear === new Date().getFullYear() ? "not-allowed" : "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                    title="Next Month"
                  >
                    ►
                  </button>
                </div>

                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedEmployeeReport(null);
                  }}
                  style={{
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "35px",
                    height: "35px",
                    cursor: "pointer",
                    fontSize: "20px",
                    fontWeight: "bold",
                    marginLeft: "15px"
                  }}
                >
                  ×
                </button>
              </div>

              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '25px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '20px',
                  borderRadius: '10px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {selectedEmployeeReport.totalGoals}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>Total Goals</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  padding: '20px',
                  borderRadius: '10px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {selectedEmployeeReport.totalCompleted}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>Completed</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #dc3545 0%, #e83e8c 100%)',
                  padding: '20px',
                  borderRadius: '10px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {selectedEmployeeReport.totalMissed}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>Missed</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                  padding: '20px',
                  borderRadius: '10px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {selectedEmployeeReport.overallPercentage}%
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>Success Rate</div>
                </div>
              </div>

              {/* Day-wise Report Table */}
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ color: '#333', marginBottom: '15px', fontSize: '18px' }}>📅 Daily Breakdown</h3>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#495057' }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#495057' }}>Day</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#495057' }}>Total Goals</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#28a745' }}>✓ Completed</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#dc3545' }}>✗ Missed</th>
                        <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#495057' }}>Success Rate</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600', color: '#495057' }}>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEmployeeReport.dailyReport.map((day, index) => (
                        <tr key={index} style={{
                          background: day.total > 0 ? 'white' : '#f8f9fa',
                          borderBottom: '1px solid #e2e8f0'
                        }}>
                          <td style={{ padding: '12px', color: '#495057' }}>
                            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td style={{ padding: '12px', color: '#6c757d', fontSize: '13px' }}>
                            {day.dayName}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#495057' }}>
                            {day.total > 0 ? day.total : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#28a745', fontWeight: '600' }}>
                            {day.total > 0 ? day.completed : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545', fontWeight: '600' }}>
                            {day.total > 0 ? day.missed : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {day.total > 0 ? (
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: day.percentage === 100 ? '#d4edda' :
                                  day.percentage >= 50 ? '#fff3cd' : '#f8d7da',
                                color: day.percentage === 100 ? '#155724' :
                                  day.percentage >= 50 ? '#856404' : '#721c24'
                              }}>
                                {day.percentage}%
                              </span>
                            ) : '-'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {day.total > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  flex: 1,
                                  height: '8px',
                                  background: '#e2e8f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${day.percentage}%`,
                                    height: '100%',
                                    background: day.percentage === 100 ? '#28a745' :
                                      day.percentage >= 50 ? '#ffc107' : '#dc3545',
                                    transition: 'width 0.3s ease'
                                  }}></div>
                                </div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedEmployeeReport(null);
                  }}
                  style={{
                    padding: "10px 25px",
                    border: "none",
                    backgroundColor: "#667eea",
                    color: "white",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "12px",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3 style={{ marginBottom: "20px", color: "#667eea" }}>
                Add/Update Goal Notes
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                  Goal: {currentGoalForNotes?.name}
                </label>
                <textarea
                  value={tempNotes}
                  onChange={(e) => setTempNotes(e.target.value)}
                  placeholder="Add notes for this goal..."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e1e5e9",
                    borderRadius: "8px",
                    fontSize: "14px",
                    resize: "vertical",
                    outline: "none",
                    transition: "border-color 0.3s ease",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                  onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
                />
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setShowNotesModal(false);
                    setCurrentGoalForNotes(null);
                    setTempNotes("");
                  }}
                  style={{
                    padding: "10px 20px",
                    border: "2px solid #ccc",
                    backgroundColor: "white",
                    color: "#666",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleNotesModalConfirm}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    backgroundColor: "#667eea",
                    color: "white",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div >
    </>
  );
}
export default Goals;

